import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiAlertTriangle, FiCheckCircle, FiExternalLink, FiXCircle } from "react-icons/fi";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/paysuccess.css";

const readPendingOrder = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(siteConfig.storage.pendingOrder) || "null");
  } catch {
    return null;
  }
};

function PaymentResult() {
  const [searchParams] = useSearchParams();
  const state = searchParams.get("state") || "unknown";
  const reason = searchParams.get("reason") || "";
  const order = useMemo(readPendingOrder, []);

  const views = {
    success: {
      icon: <FiCheckCircle />,
      eyebrow: "Returned from Stripe",
      title: "Payment return received.",
      copy: "This browser return does not prove that funds settled. We will treat the order as paid only after Stripe verifies it server-side, then send the definitive order update by email.",
      className: "success-page--pending",
    },
    cancel: {
      icon: <FiXCircle />,
      eyebrow: "Payment cancelled",
      title: "No payment confirmation was recorded.",
      copy: "Your bag remains available. You can review the address and total, then return to Stripe when you are ready.",
      className: "success-page--cancel",
    },
    failed: {
      icon: <FiAlertTriangle />,
      eyebrow: "Payment could not start",
      title: "Stripe checkout is unavailable.",
      copy: reason === "configuration"
        ? "The server is not yet configured with its Stripe secret. No payment was taken. The merchant must complete the PHP deployment configuration before accepting orders."
        : "The payment bridge could not create a Stripe session. No successful payment is assumed; please try again or contact customer care.",
      className: "success-page--failed",
    },
    unknown: {
      icon: <FiAlertTriangle />,
      eyebrow: "Payment status",
      title: "No payment result is available.",
      copy: "Open this page through the Stripe checkout return flow or go back to your bag.",
      className: "success-page--empty",
    },
  };
  const view = views[state] || views.unknown;

  return (
    <main className={`success-page ${view.className}`}>
      <section className="success-hero">
        <div className="success-hero__mark">{view.icon}</div>
        <p className="eyebrow">{view.eyebrow}</p>
        <h1>{view.title}</h1>
        <p>{view.copy}</p>
        <div className="success-actions">
          <Link className="button-light" to={state === "cancel" || state === "failed" ? "/checkout" : "/shop"}>{state === "cancel" || state === "failed" ? "Return to checkout" : "Continue shopping"}</Link>
          <Link className="success-text-link" to="/payments">How payment works <FiExternalLink /></Link>
        </div>
      </section>

      {order && (
        <section className="success-details" aria-label="Pending order details">
          <div><p className="eyebrow">Reference</p><strong>{order.orderNo}</strong></div>
          <div><p className="eyebrow">Submitted total</p><strong>{formatPrice(order.amount)}</strong></div>
          <div><p className="eyebrow">Status</p><strong>{state === "success" ? "Awaiting verification" : state === "cancel" ? "Not completed" : "Not verified"}</strong></div>
        </section>
      )}

      <section className="success-next page-shell">
        <div><FiCheckCircle /><h2>Verification comes first</h2><p>Only a trusted Stripe server event or session check can support fulfilment. URL parameters never confirm receipt of funds.</p></div>
        <div><FiAlertTriangle /><h2>Keep sensitive data off email</h2><p>Customer care may ask for the order reference, but never needs a full card number or security code.</p></div>
      </section>
    </main>
  );
}

export default PaymentResult;
